import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { BETA_TOOL_ACCESS_KEY } from "@/hooks/use-beta-tool-access";
import type { BetaToolManager, BetaToolAccessSource } from "@shared/admin-permissions";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";

const BASE = "/api/app/admin/beta-tool";
const labels: Record<BetaToolAccessSource, string> = {
  direct: "Direct permission", platform_admin: "Legacy platform-admin", super_admin: "Super-admin",
};

export function BetaToolManagers({ canAssign }: { canAssign: boolean }) {
  const client = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [removing, setRemoving] = useState<BetaToolManager | null>(null);
  const roster = useQuery<BetaToolManager[]>({ queryKey: [`${BASE}/managers`], staleTime: 0 });
  const results = useQuery<BetaToolManager[]>({
    queryKey: [`${BASE}/search`, search],
    enabled: canAssign && search.trim().length >= 2,
    queryFn: async () => {
      const res = await apiRequest("GET", `${BASE}/search?q=${encodeURIComponent(search.trim())}`);
      return res.json();
    },
  });
  const change = useMutation({
    meta: { silentSave: true },
    mutationFn: async ({ id, grant }: { id: number; grant: boolean }) => {
      await apiRequest(grant ? "POST" : "DELETE", `${BASE}/managers/${id}`);
    },
    onSuccess: (_data, { grant }) => {
      void client.invalidateQueries({ queryKey: [`${BASE}/managers`] });
      void client.invalidateQueries({ queryKey: [`${BASE}/search`] });
      void client.invalidateQueries({ queryKey: [BETA_TOOL_ACCESS_KEY] });
      void client.invalidateQueries({ queryKey: ["/api/app/auth/me"] });
      setRemoving(null);
      setSearch("");
      toast({ title: grant ? "Tool permission granted" : "Direct tool permission removed" });
    },
    onError: err => toast({ title: "Could not change tool access", description: parseErrorMessage(err, "Tool access could not be changed"), variant: "destructive" }),
  });
  const visible = (roster.data ?? []).filter(u =>
    `${u.name ?? ""} ${u.email}`.toLowerCase().includes(filter.trim().toLowerCase()));
  return <Card data-testid="beta-tool-managers">
    <CardHeader>
      <CardTitle className="text-base">Tool access</CardTitle>
      <CardDescription>
        These accounts can manage Beta Access. This is separate from beta participation and feature-gate bypass.
        Only super-admins can assign this permission.
      </CardDescription>
      <p className="text-xs text-muted-foreground">
        Removing a direct permission does not remove inherited platform-admin or super-admin authority.
        This permission grants no impersonation, dashboard-layout administration, other admin sections, or beta enrollment.
      </p>
    </CardHeader>
    <CardContent className="space-y-4">
      <Input aria-label="Filter tool managers" placeholder="Filter tool managers by name or email" value={filter} onChange={e => setFilter(e.target.value)} />
      {roster.isPending ? <p role="status">Loading tool managers…</p> :
        roster.isError ? <div role="alert">Could not load tool managers. <Button variant="outline" onClick={() => void roster.refetch()}>Retry</Button></div> :
        visible.length === 0 ? <p className="text-sm text-muted-foreground">No matching tool managers.</p> :
        <ul className="divide-y rounded-md border">
          {visible.map(u => <li key={u.id} className="p-3 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0 space-y-1">
              <p className="text-sm font-medium break-all">{u.name || u.email}</p>
              {u.name && <p className="text-xs text-muted-foreground break-all">{u.email}</p>}
              <div className="flex flex-wrap gap-1">{u.sources.map(source => <Badge key={source} variant="outline">{labels[source]}</Badge>)}</div>
              {u.sources.some(s => s !== "direct") && <p className="text-xs text-muted-foreground">Inherited authority still permits tool access.</p>}
            </div>
            {canAssign && u.sources.includes("direct") && !u.sources.includes("super_admin") &&
              <Button variant="outline" size="sm" disabled={change.isPending} onClick={() => setRemoving(u)} aria-label={`Remove direct permission for ${u.email}`}>Remove direct permission</Button>}
          </li>)}
        </ul>}
      {canAssign && <div className="space-y-2">
        <Input aria-label="Search accounts to add tool access" placeholder="Search existing accounts to add tool access" value={search} onChange={e => setSearch(e.target.value)} maxLength={200} />
        {search.trim().length >= 2 && (results.isPending ? <p role="status">Searching accounts…</p> :
          results.isError ? <p role="alert">Account search failed. Try again.</p> :
          results.data?.length === 0 ? <p className="text-sm text-muted-foreground">No matching accounts.</p> :
          <ul className="divide-y rounded-md border">{results.data?.map(u => {
            const inherited = u.sources.some(s => s !== "direct");
            const granted = u.sources.includes("direct");
            return <li key={u.id} className="p-3 flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0"><p className="text-sm break-all">{u.email}</p><p className="text-xs text-muted-foreground">{u.name}</p></div>
              <Button size="sm" disabled={change.isPending || inherited || granted} onClick={() => change.mutate({ id: u.id, grant: true })}>
                {granted ? "Already granted" : inherited ? "Inherited access" : "Add tool access"}
              </Button>
            </li>;
          })}</ul>)}
      </div>}
      <AlertDialog open={canAssign && removing !== null} onOpenChange={open => { if (!open && !change.isPending) setRemoving(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove direct tool permission?</AlertDialogTitle>
            <AlertDialogDescription>
              Remove Beta Access management permission from {removing?.email}?
              {removing?.sources.some(s => s !== "direct")
                ? " Their inherited platform-admin authority will still allow access."
                : " They will no longer be able to open or use this tool."}
              {" "}Other permissions and beta participation remain unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={change.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={change.isPending} onClick={e => {
              e.preventDefault();
              if (removing && canAssign) change.mutate({ id: removing.id, grant: false });
            }}>{change.isPending ? "Removing…" : "Remove permission"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CardContent>
  </Card>;
}
